import { useQuery } from '@tanstack/react-query';
import httpClient from '../api/httpClient';
import { API_ENDPOINTS } from '../api/endpoints';

interface PriceData {
    symbol: string;
    bid: number;
    ask: number;
    last: number;
    provider: string;
    timestamp: number;
}

interface UseCrmAllPricesReturn {
    prices: Map<string, PriceData>;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

// CRM-specific price fetching hook
// Note: instrumentIds parameter kept for backward compatibility but no longer sent to API
// The endpoint now returns all active instruments when no IDs are provided
const fetchCrmAllPrices = async (instrumentIds: string[]): Promise<Map<string, PriceData>> => {
    try {
        // Try the CRM backend endpoint first - endpoint returns all active instruments
        try {
            const response = await httpClient.post(API_ENDPOINTS.CRM.CHARTS.CURRENT_PRICES, {});

            const priceMap = new Map<string, PriceData>();

            if (response.data && response.data.data) {
                Object.entries(response.data.data).forEach(
                    ([instrumentId, priceData]: [string, any]) => {
                        if (priceData && typeof priceData === 'object') {
                            priceMap.set(instrumentId, {
                                symbol: priceData.symbol || instrumentId,
                                bid: priceData.bid || 0,
                                ask: priceData.ask || 0,
                                last: priceData.last || priceData.price || 0,
                                provider: priceData.provider || 'unknown',
                                timestamp: priceData.timestamp || Date.now(),
                            });
                        }
                    }
                );
            }

            return priceMap;
        } catch (error) {
            console.warn('CRM price endpoint failed, falling back to mock data:', error);

            // Fallback to mock data for CRM
            const mockPriceMap = new Map<string, PriceData>();
            instrumentIds.forEach(instrumentId => {
                mockPriceMap.set(instrumentId, {
                    symbol: instrumentId,
                    bid: Math.random() * 100 + 50,
                    ask: Math.random() * 100 + 50,
                    last: Math.random() * 100 + 50,
                    provider: 'mock',
                    timestamp: Date.now(),
                });
            });

            return mockPriceMap;
        }
    } catch (error) {
        console.error('Failed to fetch CRM prices:', error);
        return new Map();
    }
};

export const useCrmAllPrices = (instrumentIds: string[] = []): UseCrmAllPricesReturn => {
    const {
        data: prices = new Map(),
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['crm-prices'],
        queryFn: () => fetchCrmAllPrices(instrumentIds),
        // No polling — prices are pushed via WebSocket (usePriceSocket)
        staleTime: Infinity,
    });

    return {
        prices,
        isLoading,
        error: error?.message || null,
        refetch,
    };
};
